import './ShippingLabelForm.scss';

import { Form, FormProps, Input } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import { useCountryContext } from '@/contexts/CountryContext';
import { isValidPhoneNumber, phoneNumberMeta } from '@/utils/phoneNumbers';

import useShippingLabelForm from './useShippingLabelForm';

interface ShippingLabelProps {
  formProps: FormProps;
  edit?: boolean;
}

const ShippingLabelForm = (props: ShippingLabelProps) => {
  const { t } = useTranslation();
  const { isBrasilSelected } = useCountryContext();
  const { formProps, edit } = props;
  const { initialValues } = formProps;
  const initialValuesModified = useMemo(
    () =>
      initialValues
        ? {
            ...initialValues,
            localidad: initialValues?.localidad.id,
            pais: initialValues?.localidad.provincia.pais.id,
            provincia: initialValues?.localidad.provincia.id,
          }
        : undefined,
    [initialValues],
  );
  const {
    companySelectProps,
    paisesSelectProps,
    provinciasSelectProps,
    localidadesSelectProps,
    countryCode,
  } = useShippingLabelForm(formProps, initialValuesModified, edit);
  const postalCodeLabel = isBrasilSelected ? 'CEP' : t('Código postal');

  return (
    <Form
      {...formProps}
      layout="vertical"
      className="ShippingLabelForm"
      validateTrigger={['onFinish']}
      initialValues={edit ? initialValuesModified : { pais: 1 }}
    >
      {edit && initialValues?.id && (
        <div className="ShippingLabelFormShowDataId">
          Id de etiqueta: <span>{initialValues.id}</span>
        </div>
      )}
      <Form.Item
        label={t('Descripción')}
        name={'description'}
        rules={[
          {
            required: true,
            message: t('La descripción es requerida'),
          },
        ]}
      >
        <Input />
      </Form.Item>

      <div className="ShippingLabelFormRow">
        <Form.Item
          label={t('País')}
          name={'pais'}
          rules={[
            {
              required: true,
              message: t('El país es requerido'),
            },
          ]}
        >
          <AntdSelect {...paisesSelectProps} allowClear showSearch />
        </Form.Item>
        <Form.Item
          label={t('Provincia')}
          name={'provincia'}
          rules={[
            {
              required: true,
              message: t('La provincia es requerida'),
            },
          ]}
        >
          <AntdSelect {...provinciasSelectProps} allowClear showSearch />
        </Form.Item>
      </div>

      <div className="ShippingLabelFormRow">
        <Form.Item
          label={t('Localidad')}
          name={'localidad'}
          rules={[{ required: true, message: t('La localidad es requerida') }]}
        >
          <AntdSelect {...localidadesSelectProps} allowClear showSearch />
        </Form.Item>
        <Form.Item
          label={postalCodeLabel}
          name={'postal_code'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: postalCodeLabel }),
            },
          ]}
        >
          <Input />
        </Form.Item>
      </div>
      <Form.Item
        label={t('Dirección')}
        name={'address'}
        rules={[
          {
            required: true,
            message: t('La dirección es requerida'),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <div className="ShippingLabelFormRow">
        <Form.Item
          label={t('Email')}
          name={'email'}
          rules={[
            { type: 'email', message: t('Email inválido') },
            { required: true, message: t('El email es requerido') },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={t('Número de Teléfono')}
          name={'phone_number'}
          validateTrigger="onBlur"
          rules={[
            { required: true, message: t('El número de teléfono es requerido') },
            {
              validateTrigger: 'onBlur',
              validator: (_, value) => {
                if (!value || !countryCode) {
                  return Promise.resolve();
                }

                if (!isValidPhoneNumber(countryCode, value)) {
                  return Promise.reject(
                    new Error(t('Número de teléfono inválido para el país seleccionado')),
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Input prefix={phoneNumberMeta[countryCode]?.phoneCountry} />
        </Form.Item>
      </div>
      <div className="ShippingLabelFormRow">
        <Form.Item
          label={t('Empresa')}
          name={'company'}
          rules={[
            {
              required: true,
              message: t('La empresa es requerida'),
            },
          ]}
        >
          {/* <CompanySelector showFullName {...companySelectProps} allowClear showSearch /> */}
          <AntdSelect {...companySelectProps} allowClear showSearch />
        </Form.Item>
        <Form.Item label={t('Nombre y apellido')} name={'contact'}>
          <Input />
        </Form.Item>
      </div>
    </Form>
  );
};

export default ShippingLabelForm;
